import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Modal from 'react-bootstrap/lib/Modal';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import { ThemeConsumer } from '@adsk/bim360-matrix-react-helpers/es/theme';

import { KeyCodesUppercase } from '@adsk/bim360-matrix-react-helpers/es/consts';
import modalClose from '@adsk/bim360-matrix-react-assets/modal-close.svg';
import navigationArrow from '@adsk/bim360-matrix-react-assets/navigation-arrow.svg';
import './_ModalPreviewNavigation.scss';

import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

/**
 * @class ModalPreviewNavigationComponent
 *
 * This component allows for displaying a content in a modal dialog while providing next/previous navigation actions.
 */
class ModalPreviewNavigation extends Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);

    this.MODAL_CLASS = 'ModalPreviewNavigation__transparent-modal';
    this.handleKeyboardClick = this.handleKeyboardClick.bind(this);
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyboardClick);
  }

  componentDidUpdate() {
    if (!this.modalNode) {
      this.modalNode = document.querySelector(`.${this.MODAL_CLASS}`);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyboardClick);
  }

  handleKeyboardClick(e) {
    if (
      [this.modalNode, this.modalContainerRef].includes(document.activeElement) &&
      this.props.navigationEnabled &&
      !this.props.disabledNavigationArrows
    ) {
      switch (e.keyCode) {
        case KeyCodesUppercase.ARROW_LEFT:
          !this.props.disabledNavigationPrevArrow && this.props.previous();
          break;
        case KeyCodesUppercase.ARROW_RIGHT:
          !this.props.disabledNavigationNextArrow && this.props.next();
          break;
        default:
          break;
      }
    }
  }

  /**
   * renders the left navigation button
   */
  _renderLeftNavButton() {
    const disabled = this.props.disabledNavigationArrows || this.props.disabledNavigationPrevArrow;

    return (
      <div className="ModalPreviewNavigation__left-indicator">
        <IconButton
          className="ModalPreviewNavigation__left-icon-button"
          onClick={() => this.props.previous()}
          onMouseUp={() => this.modalContainerRef.focus()}
          disabled={disabled}
        >
          <SvgIcon width="22px" height="32px" transform="rotate(180 11 16)">
            {navigationArrow}
          </SvgIcon>
        </IconButton>
      </div>
    );
  }

  /**
   * renders the right navigation button
   */
  _renderRightNavButton() {
    const disabled = this.props.disabledNavigationArrows || this.props.disabledNavigationNextArrow;

    return (
      <div className="ModalPreviewNavigation__right-indicator">
        <IconButton
          className="ModalPreviewNavigation__right-icon-button"
          onClick={() => this.props.next()}
          onMouseUp={() => this.modalContainerRef.focus()}
          disabled={disabled}
        >
          <SvgIcon width="22px" height="32px">
            {navigationArrow}
          </SvgIcon>
        </IconButton>
      </div>
    );
  }

  /**
   * renders the close button
   */
  _renderCloseButton() {
    if (this.props.closeButtonStyle === ModalPreviewNavigation.CLOSE_BUTTON_STYLE.NO_BUTTON) {
      return null;
    }
    const { bootstrapModal: bootstrapModalProps } = this.props;
    const closeDivClass = classNames('ModalPreviewNavigation__close', {
      'ModalPreviewNavigation__close--inside':
        this.props.closeButtonStyle === ModalPreviewNavigation.CLOSE_BUTTON_STYLE.INNER_CLOSE_BUTTON
    });
    const iconSize =
      this.props.closeButtonStyle === ModalPreviewNavigation.CLOSE_BUTTON_STYLE.INNER_CLOSE_BUTTON
        ? '15px'
        : '20px';

    return (
      <div className={closeDivClass}>
        <IconButton
          data-testid="ModalPreviewNavigation__close-icon-button"
          onClick={() => (this.props.onHide || bootstrapModalProps.onHide)()}
        >
          <SvgIcon height={iconSize} width={iconSize}>
            {modalClose}
          </SvgIcon>
        </IconButton>
      </div>
    );
  }

  /**
   * Renders the component
   * @returns {VDom}
   */
  render() {
    const { bootstrapModal: bootstrapModalProps, show, onHide, onExited, container } = this.props;

    // The body ref + tabIndex + navigation buttons' onMouseUp are used in order to comply with the aria required behavior as described here - https://www.w3.org/WAI/tutorials/carousels/functionality/
    return (
      <ThemeConsumer>
        {({ theme }) => (
          <Modal
            {...{ show, onHide, onExited, container, ...bootstrapModalProps }}
            bsClass={classNames(
              bootstrapModalProps.bsClass,
              `ModalPreviewNavigation--${theme}`,
              this.MODAL_CLASS
            )}
          >
            <Modal.Body>
              <div
                ref={ref => (this.modalContainerRef = ref)}
                tabIndex="-1"
                className="ModalPreviewNavigation__container"
              >
                {this.props.navigationEnabled ? this._renderLeftNavButton() : null}
                <section className="ModalPreviewNavigation__content-container">
                  <div className="ModalPreviewNavigation__content">
                    {(bootstrapModalProps.show || this.props.show) && this.props.children}
                  </div>
                </section>
                <div className="ModalPreviewNavigation__right-panel">
                  {this._renderCloseButton()}
                  {this.props.navigationEnabled ? this._renderRightNavButton() : null}
                </div>
              </div>
            </Modal.Body>
          </Modal>
        )}
      </ThemeConsumer>
    );
  }
}
ModalPreviewNavigation.CLOSE_BUTTON_STYLE = {
  DEFAULT: '',
  NO_BUTTON: 'NO_BUTTON',
  INNER_CLOSE_BUTTON: 'INNER_CLOSE_BUTTON'
};

ModalPreviewNavigation.defaultProps = {
  navigationEnabled: false
};

ModalPreviewNavigation.propTypes = {
  bootstrapModal: PropTypes.shape({
    /**
     * A callback function for modal hide event
     */
    onHide: PropTypes.func,
    /**
     * Whether to show the modal or not
     */
    show: PropTypes.bool,
    /**
     * A custom className for the modal component. This class will be passed to react-bootstrap's Modal component.
     */
    bsClass: PropTypes.string,
    /**
     * A container component for the modal to be attached to as a child
     */
    container: PropTypes.object
  }).isRequired,
  /**
   * A callback that's called when hiding the modal. (same as `bootstrapModal.onHide` prop)
   */
  onHide: PropTypes.func,
  /**
   * A callback that's called when the modal has exited.
   */
  onExited: PropTypes.func,
  /**
   * Whether or not to show the modal. (same as `bootstrapModal.show` prop)
   */
  show: PropTypes.bool,
  /**
   * A container component for the modal to be attached to as a child. (same as `bootstrapModal.show` prop)
   */
  container: PropTypes.object,
  /**
   * A callback function for previous button click event
   */
  previous: PropTypes.func,
  /**
   * A callback function for next button click event
   */
  next: PropTypes.func,
  /**
   * Determines whether navigation is enabled. If false is passed, navigation is disabled and arrows are not visible. Defaults to false.
   */
  navigationEnabled: PropTypes.bool,
  /**
   * Disables navigation next arrow (still shows them, but disabled). Disables navigation
   */
  disabledNavigationNextArrow: PropTypes.bool,
  /**
   * Disables navigation back arrow (still shows them, but disabled). Disables navigation
   */
  disabledNavigationPrevArrow: PropTypes.bool,
  /**
   * Disables navigation next arrow (still shows them, but disabled). Disables navigation
   */
  disabledNavigationArrows: PropTypes.bool,
  /**
   * Style of modal's close button. Can be one of ModalPreviewNavigation.CLOSE_BUTTON_STYLE.
   */
  closeButtonStyle: PropTypes.oneOf(objectValues(ModalPreviewNavigation.CLOSE_BUTTON_STYLE)),
  /**
   * Modal content
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

export default ModalPreviewNavigation;
